import { Container, IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { BaseScalableView } from "../../../../common/BaseScalableView";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { ILogger } from "../../../../../logger/ILogger";
import { ButtonWithScaleAnimationAndText } from "../../../../common/components/ButtonWithScaleAnimationAndText";
import { ButtonWithScaleAnimation } from "../../../../common/components/ButtonWithScaleAnimation";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { GameFont } from "../../../../common/GameFont";
import { FullscreenInputBlocker } from "../../../../game/components/FullscreenInputBlocker";
import { gsap } from "gsap";
import { ISignOutController } from "../controllers/ISignOutController";
import { SignOutResourcePackage } from "../resources/SignOutResourcePackage";

export class SignOutPopupView extends BaseScalableView<boolean> {
    private static readonly HEADER_TEXT = "SIGN OUT";
    private static readonly DESCRIPTION_TEXT = "ARE YOU SURE YOU WANT TO";
    private static readonly DESCRIPTION_RED_TEXT = "SIGN OUT";

    private readonly commonResources: CommonResourcesPackage;
    private readonly signOutResources: SignOutResourcePackage;
    private readonly signOutController: ISignOutController;
    private readonly logger: ILogger;

    private viewBackground: Sprite;
    private header: Text;
    private descriptionText: Text;
    private descriptionRedText: Text;
    private questionText: Text;
    private frame: Container;
    private yesButton: ButtonWithScaleAnimationAndText;
    private noButton: ButtonWithScaleAnimationAndText;
    private closeButton: ButtonWithScaleAnimation;
    private leftLight: Sprite;
    private rightLight: Sprite;

    private viewAnimation: gsap.core.Timeline;

    public constructor(
        operationControls: PromiseCompletionSource<boolean>,
        commonResources: CommonResourcesPackage,
        signOutResources: SignOutResourcePackage,
        canvasScaler: ICanvasScaler,
        signOutController: ISignOutController,
        logger: ILogger
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.signOutResources = signOutResources;
        this.signOutController = signOutController;
        this.logger = logger;
        this.viewAnimation = new gsap.core.Timeline({ paused: true });
        this.viewAnimation.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.3, ease: "power1.in" });
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public start(): void {
        this.show();
    }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = "static";

        this.header = new Text(SignOutPopupView.HEADER_TEXT, new OrangeTextStyle());
        this.header.angle = -5;
        this.header.skew.x = -0.08;

        this.frame = new Container();
        const frameBg = new Sprite(this.signOutResources.assets_views_sign_out_base_frame_png);

        const textContainer = new Container();
        this.descriptionText = new Text(SignOutPopupView.DESCRIPTION_TEXT, new WhiteTextStyle());
        this.descriptionRedText = new Text(SignOutPopupView.DESCRIPTION_RED_TEXT, new OrangeTextStyle());
        this.descriptionRedText.style.fontSize = 44;
        this.questionText = new Text("?", new WhiteTextStyle());
        textContainer.addChild(this.descriptionText, this.descriptionRedText, this.questionText);

        this.yesButton = new ButtonWithScaleAnimationAndText(this.commonResources.assets_common_ui_button_green_png, "YES", new ButtonTextStyle());
        this.yesButton.width = 314;
        this.yesButton.height = 161;
        this.yesButton.onUp.add(this.okButtonClickHandle, this);
        this.noButton = new ButtonWithScaleAnimationAndText(this.commonResources.assets_common_ui_button_orange_png, "NO", new ButtonTextStyle());
        this.noButton.width = 296;
        this.noButton.height = 144;
        this.noButton.onUp.add(this.closeButtonClickHandle, this);
        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.hitArea = new Rectangle(-this.closeButton.width * 0.5, -this.closeButton.height, this.closeButton.width * 2, this.closeButton.height * 2);
        this.closeButton.onUp.add(this.closeButtonClickHandle, this);

        this.leftLight = new Sprite(this.commonResources.assets_common_ui_lights_light_left_png);
        this.rightLight = new Sprite(this.commonResources.assets_common_ui_lights_light_left_png);
        this.rightLight.scale.x = -1;

        this.frame.addChild(frameBg, textContainer, this.yesButton, this.noButton);
        this.addChild(this.viewBackground, this.header, this.frame, this.closeButton, this.leftLight, this.rightLight);
    }

    private async okButtonClickHandle(): Promise<void> {
        try {
            await this.signOutController.signOut();
        } catch (error: unknown) {
            this.logger.error(error);
        } finally {
            await this.hideView();
            this.operationControls.complete(true);
        }
    }

    private async closeButtonClickHandle(): Promise<void> {
        await this.hideView();
        this.operationControls.complete(false);
    }

    private async show(): Promise<void> {
        await this.viewAnimation.play();
    }

    private async hideView(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        await this.viewAnimation.reverse();
    }

    private layout(width: number, _height: number): void {
        this.header.position.set(215, 533);
        this.frame.position.set(57, 630);
        this.descriptionText.position.set(100, 173);
        this.descriptionRedText.position.set(473, 173);
        this.questionText.position.set(593, 173);
        this.yesButton.position.set(34, 330);
        this.noButton.position.set(375, 333);
        this.closeButton.position.set(375, 1219);
        this.leftLight.position.set(0, 640);
        this.rightLight.position.set(width, 640);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.closeButton.onUp.remove(this.closeButtonClickHandle, this);
        this.yesButton.onUp.remove(this.okButtonClickHandle, this);
        this.noButton.onUp.remove(this.closeButtonClickHandle, this);
        super.destroy(options);
    }
}

class OrangeTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: [
                "#fe7934",
                "#f72775"
            ],
            fillGradientType: 1,
            fillGradientStops: [
                0,
                0.8
            ],
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 148
        }));
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super({
            dropShadow: true,
            dropShadowAngle: 0.2,
            dropShadowBlur: 5,
            dropShadowDistance: 2,
            dropShadowAlpha: 0.5,
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 58,
            miterLimit: 0
        });
    }
}

class WhiteTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 44
        }));
    }
}
